<script setup>
import {
  ref,
  computed,
  reactive,
  watch,
  nextTick,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import Draggable from 'vuedraggable';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import TaskRow from './TaskRow.vue';
import TaskDetails from './TaskDetails.vue';

const store = useStore();
const { t } = useI18n();

const viewMode = ref('list'); // 'list' | 'board'
const groupBy = ref('status'); // 'status' | 'priority' | 'assignee'
const sortBy = ref('created'); // 'created' | 'due_date' | 'priority' | 'title'
const query = ref('');
const activeFilter = ref('all');
const collapsed = reactive({});

// Per-column inline add
const addingKey = ref(null);
const addTitle = ref('');
const addInput = ref(null);

const columns = ref([]);

const tasks = computed(() => store.getters.getTasks);
const uiFlags = computed(() => store.getters.getTaskUIFlags);
const currentUserId = computed(() => store.getters.getCurrentUser?.id);

const STATUS_ORDER = ['open', 'in_progress', 'in_review', 'done', 'cancelled'];
const STATUS_DOT = {
  open: 'bg-n-slate-9',
  in_progress: 'bg-n-blue-9',
  in_review: 'bg-n-amber-9',
  done: 'bg-n-teal-9',
  cancelled: 'bg-n-slate-8',
};
const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];
const PRIORITY_DOT = {
  urgent: 'bg-n-ruby-9',
  high: 'bg-n-amber-9',
  medium: 'bg-n-blue-9',
  low: 'bg-n-slate-9',
};
const PRIORITY_RANK = { urgent: 4, high: 3, medium: 2, low: 1 };

const statusLabel = s => t(`TASKS.STATUS.${s.toUpperCase()}`);
const priorityLabel = p => t(`TASKS.PRIORITY.${p.toUpperCase()}`);

// Cross-status quick filters
const QUICK_FILTERS = ['all', 'mine', 'overdue', 'blocked'];
const quickFilterLabel = key =>
  ({
    all: t('TASKS.FILTERS.ALL'),
    mine: t('TASKS.FILTERS.MINE'),
    overdue: t('TASKS.FILTERS.OVERDUE'),
    blocked: t('TASKS.BADGES.BLOCKED'),
  })[key];

const matchesQuickFilter = task => {
  switch (activeFilter.value) {
    case 'mine':
      return task.assignee_id === currentUserId.value;
    case 'overdue':
      return task.overdue;
    case 'blocked':
      return task.blocked;
    default:
      return true;
  }
};

const sortTasks = list => {
  const arr = [...list];
  switch (sortBy.value) {
    case 'due_date':
      return arr.sort(
        (a, b) =>
          (a.due_date ? Date.parse(a.due_date) : Infinity) -
          (b.due_date ? Date.parse(b.due_date) : Infinity)
      );
    case 'priority':
      return arr.sort(
        (a, b) => PRIORITY_RANK[b.priority] - PRIORITY_RANK[a.priority]
      );
    case 'title':
      return arr.sort((a, b) => a.title.localeCompare(b.title));
    default:
      return arr.sort(
        (a, b) => Date.parse(b.created_at) - Date.parse(a.created_at)
      );
  }
};

const columnDefs = base => {
  if (groupBy.value === 'priority') {
    return PRIORITY_ORDER.map(key => ({
      key,
      label: priorityLabel(key),
      dot: PRIORITY_DOT[key],
    }));
  }
  if (groupBy.value === 'assignee') {
    const seen = new Map();
    base.forEach(task => {
      const key = task.assignee_id ? String(task.assignee_id) : 'unassigned';
      if (!seen.has(key)) {
        seen.set(key, {
          key,
          label: task.assignee?.name || t('TASKS.FIELDS.UNASSIGNED'),
          dot: 'bg-n-slate-9',
        });
      }
    });
    return [...seen.values()];
  }
  return STATUS_ORDER.map(key => ({
    key,
    label: statusLabel(key),
    dot: STATUS_DOT[key],
  }));
};

const inColumn = (task, key) => {
  if (groupBy.value === 'priority') return task.priority === key;
  if (groupBy.value === 'assignee') {
    return key === 'unassigned'
      ? !task.assignee_id
      : String(task.assignee_id) === key;
  }
  return task.status === key;
};

const buildColumns = () => {
  const q = query.value.trim().toLowerCase();
  const base = sortTasks(
    tasks.value
      .filter(matchesQuickFilter)
      .filter(task => !q || task.title.toLowerCase().includes(q))
  );
  columns.value = columnDefs(base).map(def => ({
    ...def,
    items: base.filter(task => inColumn(task, def.key)),
  }));
};

watch([tasks, groupBy, sortBy, query, activeFilter], buildColumns, {
  immediate: true,
});

const totalCount = computed(() =>
  columns.value.reduce((sum, c) => sum + c.items.length, 0)
);

const groupByOptions = computed(() => [
  { value: 'status', label: t('TASKS.FIELDS.STATUS') },
  { value: 'priority', label: t('TASKS.FIELDS.PRIORITY') },
  { value: 'assignee', label: t('TASKS.FIELDS.ASSIGNEE') },
]);
const sortOptions = computed(() => [
  { value: 'created', label: t('TASKS.SORT.CREATED') },
  { value: 'due_date', label: t('TASKS.SORT.DUE_DATE') },
  { value: 'priority', label: t('TASKS.SORT.PRIORITY') },
  { value: 'title', label: t('TASKS.SORT.TITLE') },
]);
const groupByLabel = computed(
  () => groupByOptions.value.find(o => o.value === groupBy.value)?.label
);
const sortLabel = computed(
  () => sortOptions.value.find(o => o.value === sortBy.value)?.label
);

// Task detail / create opens in a modal over the list (no page navigation).
const selectedTaskId = ref(null);
const creatingTask = ref(false);
const isTaskModalOpen = computed(
  () => Boolean(selectedTaskId.value) || creatingTask.value
);
const openTask = task => {
  creatingTask.value = false;
  selectedTaskId.value = task.display_id;
};
const openCreate = () => {
  selectedTaskId.value = null;
  creatingTask.value = true;
};
const closeTaskModal = () => {
  selectedTaskId.value = null;
  creatingTask.value = false;
  store.dispatch('fetchTasks'); // reconcile any edits made in the modal
};
const onTaskCreated = displayId => {
  creatingTask.value = false;
  selectedTaskId.value = displayId;
  store.dispatch('fetchTasks');
};
const onModalNavigate = displayId => {
  creatingTask.value = false;
  selectedTaskId.value = displayId;
};
const onModalKeydown = e => {
  if (e.key === 'Escape' && isTaskModalOpen.value) closeTaskModal();
};

const toggleCollapse = key => {
  collapsed[key] = !collapsed[key];
};

const toggleDone = async task => {
  const status = task.status === 'done' ? 'open' : 'done';
  await store.dispatch('updateTask', { id: task.display_id, status });
};

// Apply the grouped attribute when a task is dragged into a column.
const applyGroupChange = (task, key) => {
  if (!task) return;
  if (groupBy.value === 'priority') {
    if (task.priority !== key) {
      store.dispatch('updateTask', { id: task.display_id, priority: key });
    }
  } else if (groupBy.value === 'assignee') {
    const assigneeId = key === 'unassigned' ? null : Number(key);
    if (assigneeId !== (task.assignee_id ?? null)) {
      store.dispatch('assignTask', { id: task.display_id, assigneeId });
    }
  } else if (task.status !== key) {
    store.dispatch('updateTask', { id: task.display_id, status: key });
  }
};

const onColumnChange = (column, evt) => {
  if (evt.added) applyGroupChange(evt.added.element, column.key);
};

const focusAddInput = () => {
  const el = Array.isArray(addInput.value) ? addInput.value[0] : addInput.value;
  el?.focus?.();
};

const startAdd = key => {
  addingKey.value = key;
  addTitle.value = '';
  nextTick(focusAddInput);
};

const cancelAdd = () => {
  addingKey.value = null;
  addTitle.value = '';
};

const submitAdd = async column => {
  const title = addTitle.value.trim();
  if (!title) return;
  const payload = { title };
  if (groupBy.value === 'status') payload.status = column.key;
  if (groupBy.value === 'priority') payload.priority = column.key;
  if (groupBy.value === 'assignee' && column.key !== 'unassigned') {
    payload.assignee_id = Number(column.key);
  }
  await store.dispatch('createTask', payload);
  addTitle.value = '';
  await nextTick();
  focusAddInput();
};

onMounted(() => {
  store.dispatch('fetchTasks');
  store.dispatch('agents/get');
  document.addEventListener('keydown', onModalKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', onModalKeydown);
});
</script>

<template>
  <div class="flex flex-col w-full h-full bg-n-background">
    <!-- Toolbar -->
    <header
      class="flex flex-col gap-3 px-6 pt-5 pb-3 border-b border-n-weak shrink-0"
    >
      <div class="flex items-center justify-between gap-3">
        <h1
          class="flex items-center gap-2 text-xl font-semibold text-n-slate-12"
        >
          {{ t('TASKS.HEADER_TITLE') }}
          <span class="text-base font-normal text-n-slate-10">
            {{ totalCount }}
          </span>
        </h1>
        <div class="flex items-center gap-2">
          <!-- View toggle -->
          <div class="flex p-0.5 rounded-lg bg-n-alpha-1">
            <button
              v-for="mode in ['list', 'board']"
              :key="mode"
              type="button"
              class="flex items-center gap-1.5 px-2.5 h-7 text-sm rounded-md transition-colors"
              :class="
                viewMode === mode
                  ? 'bg-n-solid-1 text-n-slate-12 shadow-sm'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="viewMode = mode"
            >
              <span
                :class="
                  mode === 'list' ? 'i-lucide-list' : 'i-lucide-columns-3'
                "
              />
              {{ t(`TASKS.VIEWS.${mode.toUpperCase()}`) }}
            </button>
          </div>
          <div class="relative w-52 max-w-full">
            <span
              class="absolute -translate-y-1/2 i-lucide-search left-2.5 top-1/2 text-n-slate-10"
            />
            <input
              v-model="query"
              type="text"
              :placeholder="t('TASKS.SEARCH_PLACEHOLDER')"
              class="w-full h-8 pl-8 pr-3 text-sm border-none rounded-lg reset-base outline-1 outline outline-n-weak bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand"
            />
          </div>
          <Button
            :label="t('TASKS.ACTIONS.NEW')"
            icon="i-lucide-plus"
            size="sm"
            color="blue"
            @click="openCreate"
          />
        </div>
      </div>

      <div class="flex flex-wrap items-center justify-between gap-3">
        <!-- Quick filters -->
        <nav class="flex items-center gap-1.5">
          <button
            v-for="key in QUICK_FILTERS"
            :key="key"
            class="px-2.5 h-7 text-sm rounded-lg transition-colors"
            :class="
              activeFilter === key
                ? 'bg-n-brand/10 text-n-blue-text font-medium'
                : 'text-n-slate-11 hover:bg-n-alpha-1'
            "
            @click="activeFilter = key"
          >
            {{ quickFilterLabel(key) }}
          </button>
        </nav>

        <!-- Group by + sort -->
        <div class="flex items-center gap-2">
          <span class="text-xs text-n-slate-10">{{ t('TASKS.GROUP_BY') }}</span>
          <SelectMenu
            v-model="groupBy"
            :options="groupByOptions"
            :label="groupByLabel"
            sub-menu-position="bottom"
          />
          <span class="text-xs text-n-slate-10">{{ t('TASKS.SORT_BY') }}</span>
          <SelectMenu
            v-model="sortBy"
            :options="sortOptions"
            :label="sortLabel"
            sub-menu-position="bottom"
          />
        </div>
      </div>
    </header>

    <p
      v-if="!totalCount && !uiFlags.isFetching"
      class="p-12 text-sm italic text-center text-n-slate-10"
    >
      {{ tasks.length ? t('TASKS.NO_RESULTS') : t('TASKS.EMPTY') }}
    </p>

    <!-- ===================== LIST VIEW ===================== -->
    <div v-else-if="viewMode === 'list'" class="flex-1 overflow-y-auto">
      <section
        v-for="column in columns"
        :key="column.key"
        class="border-b border-n-weak last:border-b-0"
      >
        <button
          type="button"
          class="sticky top-0 z-10 flex items-center w-full gap-2 px-6 py-2 text-left border-b border-n-weak bg-n-background/95 backdrop-blur"
          @click="toggleCollapse(column.key)"
        >
          <span
            class="i-lucide-chevron-down text-n-slate-10 transition-transform"
            :class="{ '-rotate-90': collapsed[column.key] }"
          />
          <span class="w-2 h-2 rounded-full" :class="column.dot" />
          <span class="text-sm font-semibold uppercase text-n-slate-12">
            {{ column.label }}
          </span>
          <span class="text-sm text-n-slate-10">{{ column.items.length }}</span>
        </button>

        <div v-show="!collapsed[column.key]">
          <Draggable
            :list="column.items"
            :group="{ name: 'tasks' }"
            item-key="id"
            :animation="150"
            ghost-class="opacity-40"
            class="divide-y divide-n-weak/60"
            @change="onColumnChange(column, $event)"
          >
            <template #item="{ element }">
              <div>
                <TaskRow
                  :task="element"
                  layout="row"
                  @open="openTask"
                  @toggle="toggleDone"
                />
              </div>
            </template>
          </Draggable>

          <!-- Inline add -->
          <div class="px-6 py-1.5 border-t border-n-weak/60">
            <input
              v-if="addingKey === column.key"
              ref="addInput"
              v-model="addTitle"
              type="text"
              :placeholder="t('TASKS.CREATE_PROMPT_TITLE')"
              class="w-full px-2 py-1 text-sm border-none rounded-md reset-base bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand outline-1 outline outline-transparent"
              @keyup.enter="submitAdd(column)"
              @keyup.esc="cancelAdd"
            />
            <button
              v-else
              type="button"
              class="inline-flex items-center gap-1.5 px-2 py-1 text-sm rounded-md text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-1"
              @click="startAdd(column.key)"
            >
              <span class="i-lucide-plus" />
              {{ t('TASKS.ACTIONS.ADD') }}
            </button>
          </div>
        </div>
      </section>
    </div>

    <!-- ===================== BOARD VIEW ===================== -->
    <div v-else class="flex flex-1 gap-4 px-6 py-4 overflow-x-auto">
      <section
        v-for="column in columns"
        :key="column.key"
        class="flex flex-col w-72 max-h-full shrink-0 rounded-xl bg-n-alpha-1"
      >
        <div class="flex items-center gap-2 px-3 py-2.5 shrink-0">
          <span class="w-2 h-2 rounded-full" :class="column.dot" />
          <span class="text-sm font-semibold uppercase text-n-slate-12">
            {{ column.label }}
          </span>
          <span class="text-sm text-n-slate-10">{{ column.items.length }}</span>
        </div>

        <Draggable
          :list="column.items"
          :group="{ name: 'tasks' }"
          item-key="id"
          :animation="150"
          ghost-class="opacity-40"
          class="flex flex-col flex-1 gap-2 px-2 pb-2 overflow-y-auto min-h-[40px]"
          @change="onColumnChange(column, $event)"
        >
          <template #item="{ element }">
            <TaskRow
              :task="element"
              layout="card"
              @open="openTask"
              @toggle="toggleDone"
            />
          </template>
        </Draggable>

        <!-- Inline add -->
        <div class="px-2 pb-2 shrink-0">
          <input
            v-if="addingKey === column.key"
            ref="addInput"
            v-model="addTitle"
            type="text"
            :placeholder="t('TASKS.CREATE_PROMPT_TITLE')"
            class="w-full px-2 py-1.5 text-sm border-none rounded-lg reset-base bg-n-solid-1 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-n-brand outline-1 outline outline-transparent"
            @keyup.enter="submitAdd(column)"
            @keyup.esc="cancelAdd"
          />
          <button
            v-else
            type="button"
            class="inline-flex items-center w-full gap-1.5 px-2 py-1.5 text-sm rounded-lg text-n-slate-10 hover:text-n-slate-12 hover:bg-n-alpha-2"
            @click="startAdd(column.key)"
          >
            <span class="i-lucide-plus" />
            {{ t('TASKS.ACTIONS.ADD') }}
          </button>
        </div>
      </section>
    </div>

    <!-- Task detail / create modal -->
    <Teleport to="body">
      <div
        v-if="isTaskModalOpen"
        class="fixed inset-0 z-[100] flex items-center justify-center p-4 sm:p-8 bg-n-alpha-black1 backdrop-blur-sm"
        @click.self="closeTaskModal"
      >
        <div
          class="relative flex flex-col w-full shadow-2xl rounded-2xl bg-n-background outline outline-1 outline-n-container"
          :class="
            creatingTask
              ? 'max-w-2xl max-h-[85vh] overflow-y-auto'
              : 'max-w-6xl h-[88vh] overflow-hidden'
          "
        >
          <button
            type="button"
            class="absolute z-20 flex items-center justify-center w-8 h-8 rounded-lg top-3 right-3 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12"
            @click="closeTaskModal"
          >
            <span class="i-lucide-x" />
          </button>
          <TaskDetails
            :key="creatingTask ? 'create' : selectedTaskId"
            :task-id="selectedTaskId"
            :create="creatingTask"
            embedded
            @navigate="onModalNavigate"
            @created="onTaskCreated"
            @close="closeTaskModal"
          />
        </div>
      </div>
    </Teleport>
  </div>
</template>
